import {
  buildFaceMeshAlpha,
  buildPolygonAlphaMask,
  dilateMask,
  mergeMasksMax,
  buildEyeFallbackAlpha,
  createLayerFromAlpha,
  createScleraLayerFromAlpha,
  sampleIrisPalette,
  countOpaque,
  filterTrianglesByAlpha,
  softenAlphaEdges
} from './imageUtils'
import { clampByte } from './helpers'
import {
  IRIS_INDICES,
  EYE_LAYER_POLYGONS,
  FACE_MESH_CONTOUR_PATHS
} from '../avatarInspector/featureIndices'
import { computeHairAlpha, buildHairMeshFromAlpha, computeHairMeshDepth } from './segmenter'
import { expandFaceOvalPointsByIndices } from '../faceMaskUtils'
import { buildIrisCutAlphaMask, applyAlphaCutout } from './eyeCutout'

async function loadMaskAlphaFromSrc(src, width, height) {
  if (typeof document === 'undefined') return null
  if (!src || typeof src !== 'string' || !src.length) return null
  if (!(width > 0) || !(height > 0)) return null
  try {
    const maskImg = await new Promise((resolve) => {
      try {
        const img = new Image()
        if (!src.startsWith('data:') && !src.startsWith('blob:')) {
          img.crossOrigin = 'anonymous'
        }
        img.onload = () => resolve(img)
        img.onerror = () => resolve(null)
        img.src = src
      } catch {
        resolve(null)
      }
    })
    if (!maskImg) return null
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    if (!ctx) return null
    ctx.clearRect(0, 0, width, height)
    ctx.drawImage(maskImg, 0, 0, width, height)

    const imgData = ctx.getImageData(0, 0, width, height)
    const alpha = new Uint8ClampedArray(width * height)
    let hasTransparent = false
    for (let i = 0; i < width * height; i += 1) {
      const a = imgData.data[i * 4 + 3] || 0
      alpha[i] = a
      if (a < 255) hasTransparent = true
    }
    if (!hasTransparent) {
      // Support mask images encoded as opaque black/white.
      for (let i = 0; i < width * height; i += 1) {
        const di = i * 4
        const r = imgData.data[di] || 0
        const g = imgData.data[di + 1] || 0
        const b = imgData.data[di + 2] || 0
        const luma = 0.299 * r + 0.587 * g + 0.114 * b
        alpha[i] = luma >= 128 ? 255 : 0
      }
    }
    return alpha
  } catch {
    return null
  }
}

export async function buildAvatarHeadLayer({
  avatarImage,
  avatarRig,
  settings,
  hairSegmenterImage,
  debug
}) {
  if (!avatarImage?.img || !avatarImage.safe || !avatarRig) return null
  try {
    const boostHairOpacity = (alpha, { edgeQuality = 'smooth', enhance = false } = {}) => {
      if (!alpha?.length) return alpha
      const out = new Uint8ClampedArray(alpha.length)
      const midGain = edgeQuality === 'off' ? 1.08 : edgeQuality === 'supersample' ? 1.2 : 1.14
      const solidGain = enhance ? 1.28 : edgeQuality === 'supersample' ? 1.24 : 1.18
      for (let i = 0; i < alpha.length; i += 1) {
        const a = alpha[i] || 0
        if (a <= 0) {
          out[i] = 0
          continue
        }
        if (a >= 250) {
          out[i] = 255
          continue
        }
        if (a > 120) {
          out[i] = Math.min(255, Math.round(a * solidGain))
          continue
        }
        if (a > 48) {
          out[i] = Math.min(255, Math.round(a * midGain + 8))
          continue
        }
        out[i] = Math.min(255, Math.round(a * 1.03))
      }
      return out
    }

    const width = avatarRig.imageWidth
    const height = avatarRig.imageHeight
    const faceMeshAlpha = buildFaceMeshAlpha(width, height, avatarRig)
    const faceAlpha = dilateMask(faceMeshAlpha, width, height, 2)

    const { hairAlpha, source } = computeHairAlpha(
      hairSegmenterImage,
      avatarImage.img,
      width,
      height,
      avatarRig,
      {
        maskSampling: settings?.hairMaskSampling || 'bilinear',
        disableHairHeuristic: settings?.hairHeuristicFallback ? false : true
      }
    )
    let hairAlphaInput = hairAlpha
    let refinedHairMode = 'none'
    const refineHairMaskSrc =
      typeof settings?.precomputedAvatarHairMaskSrc === 'string' &&
      settings.precomputedAvatarHairMaskSrc.length
        ? settings.precomputedAvatarHairMaskSrc
        : null
    const refineCutoutSrc =
      typeof settings?.precomputedAvatarCutoutSrc === 'string' &&
      settings.precomputedAvatarCutoutSrc.length
        ? settings.precomputedAvatarCutoutSrc
        : null
    const refineMaskSrc = refineHairMaskSrc || refineCutoutSrc || null
    const refineUsesDedicatedHairMask = !!refineHairMaskSrc
    if (refineMaskSrc) {
      try {
        const refinedHairAlpha = await loadMaskAlphaFromSrc(refineMaskSrc, width, height)
        if (refinedHairAlpha) {
          if (refineUsesDedicatedHairMask) {
            // When refine provides an explicit hair mask, treat it as authoritative.
            // Face/eyebrow animation remains independent because face layers are separate.
            hairAlphaInput = refinedHairAlpha
            refinedHairMode = 'replace'
          } else {
            // Legacy fallback for precomputed cutout: constrain existing hair extraction.
            const gated = new Uint8ClampedArray(width * height)
            for (let i = 0; i < gated.length; i += 1) {
              const base = hairAlpha[i] || 0
              const mask = refinedHairAlpha[i] || 0
              gated[i] = Math.round((base * mask) / 255)
            }
            hairAlphaInput = gated
            refinedHairMode = 'gate'
          }
        }
      } catch {
        /* ignore */
      }
    }
    const edgeQuality = settings?.hairEdgeQuality || 'smooth'
    const softenRadius = edgeQuality === 'supersample' ? 2 : edgeQuality === 'smooth' ? 1 : 0
    const hairOnlyAlphaBase =
      softenRadius > 0
        ? softenAlphaEdges(hairAlphaInput, width, height, { radius: softenRadius })
        : hairAlphaInput
    const hairOnlyAlpha = boostHairOpacity(hairOnlyAlphaBase, {
      edgeQuality,
      enhance: settings?.hairEnhance === true
    })
    const headAlpha = new Uint8ClampedArray(width * height)
    for (let p = 0; p < width * height; p += 1) {
      headAlpha[p] = Math.max(faceAlpha[p], hairOnlyAlpha[p])
    }
    const eyeAlphaBase = buildPolygonAlphaMask(width, height, avatarRig.pointsPx, [
      EYE_LAYER_POLYGONS.left,
      EYE_LAYER_POLYGONS.right
    ])
    const eyeMeshAlpha = dilateMask(eyeAlphaBase, width, height, 1)
    const eyeMeshOpaque = countOpaque(eyeMeshAlpha)
    const eyeFallbackAlpha = buildEyeFallbackAlpha(width, height, avatarRig)
    const eyeFallbackOpaque = countOpaque(eyeFallbackAlpha)
    const eyeAlpha =
      eyeMeshOpaque >= 48 ? eyeMeshAlpha : mergeMasksMax(eyeMeshAlpha, eyeFallbackAlpha)

    const irisAlpha = buildIrisCutAlphaMask({
      width,
      height,
      pointsPx: avatarRig.pointsPx,
      eyeAlpha,
      softEdgePx: Number.isFinite(Number(settings?.eyeCutSoftPx))
        ? Number(settings.eyeCutSoftPx)
        : 1.2,
      growPx: Number.isFinite(Number(settings?.eyeCutGrowPx))
        ? Number(settings.eyeCutGrowPx)
        : 0.35
    })
    const scleraAlpha = new Uint8ClampedArray(eyeAlpha)
    const totalEyeCut = irisAlpha
    const irisTriangles =
      avatarRig.meshCount > IRIS_INDICES.rightRing[3]
        ? [
            IRIS_INDICES.leftCenter,
            IRIS_INDICES.leftRing[0],
            IRIS_INDICES.leftRing[1],
            IRIS_INDICES.leftCenter,
            IRIS_INDICES.leftRing[1],
            IRIS_INDICES.leftRing[2],
            IRIS_INDICES.leftCenter,
            IRIS_INDICES.leftRing[2],
            IRIS_INDICES.leftRing[3],
            IRIS_INDICES.leftCenter,
            IRIS_INDICES.leftRing[3],
            IRIS_INDICES.leftRing[0],
            IRIS_INDICES.rightCenter,
            IRIS_INDICES.rightRing[0],
            IRIS_INDICES.rightRing[1],
            IRIS_INDICES.rightCenter,
            IRIS_INDICES.rightRing[1],
            IRIS_INDICES.rightRing[2],
            IRIS_INDICES.rightCenter,
            IRIS_INDICES.rightRing[2],
            IRIS_INDICES.rightRing[3],
            IRIS_INDICES.rightCenter,
            IRIS_INDICES.rightRing[3],
            IRIS_INDICES.rightRing[0]
          ]
        : []

    // Build a simple facial-oval mask (no reinforcement, no hair logic)
    const faceOvalPoints = expandFaceOvalPointsByIndices(
      avatarRig.pointsPx,
      FACE_MESH_CONTOUR_PATHS
    )
    const faceOvalAlpha = buildPolygonAlphaMask(width, height, faceOvalPoints, [
      FACE_MESH_CONTOUR_PATHS.faceOval
    ])
    // Union of a gently dilated mesh face and an inflated oval for full coverage at yaw
    const faceMeshInflated = dilateMask(faceMeshAlpha, width, height, 2)
    const faceOvalInflated = dilateMask(faceOvalAlpha, width, height, 3)
    let faceBaseAlphaRaw = mergeMasksMax(faceMeshInflated, faceOvalInflated)
    // Remove inner mouth/teeth region from the face to avoid distortion while talking.
    // If the avatar mouth is configured to be closed-at-rest, keep the mouth area in the face so
    // we don't create a visible gap when user closes lips. We'll handle occlusion dynamically.
    const restClosed = settings?.mouthRestClose !== false
    const smallRestScale = Number.isFinite(Number(settings?.mouthRestScale))
      ? Number(settings.mouthRestScale) < 0.2
      : false
    if (settings?.mouthCutEnable !== false && !restClosed && !smallRestScale) {
      try {
        let mouthInner = buildPolygonAlphaMask(width, height, avatarRig.pointsPx, [
          FACE_MESH_CONTOUR_PATHS.lipsInner
        ])
        const mouthDilate = Math.max(
          0,
          Math.min(6, Math.floor(Number(settings?.mouthCutDilatePx ?? 6) || 6))
        )
        if (mouthDilate > 0) mouthInner = dilateMask(mouthInner, width, height, mouthDilate)
        for (let p = 0; p < faceBaseAlphaRaw.length; p += 1) {
          if (mouthInner[p] > 0) faceBaseAlphaRaw[p] = 0
        }
      } catch {
        if (debug) debug('[headBuilder] mouth cutout skipped due to error')
      }
    }
    // Light feather for clean edges in camera view
    const faceBaseAlpha = softenAlphaEdges(faceBaseAlphaRaw, width, height, { radius: 1 })
    const faceAlphaFinal = faceBaseAlpha
    const faceNoIrisAlpha = applyAlphaCutout(faceBaseAlpha, totalEyeCut)

    const faceCanvas = createLayerFromAlpha(avatarImage.img, width, height, faceBaseAlpha)
    const faceNoIrisCanvas = createLayerFromAlpha(avatarImage.img, width, height, faceNoIrisAlpha)
    const scleraCanvas = createScleraLayerFromAlpha(avatarImage.img, width, height, scleraAlpha)
    const irisCanvas = createLayerFromAlpha(avatarImage.img, width, height, irisAlpha)
    const pupilPalette = sampleIrisPalette(avatarImage.img, width, height, avatarRig.pointsPx)
    const faceOpaque = countOpaque(faceAlphaFinal)
    const hairOpaque = countOpaque(hairOnlyAlpha)
    const hairBackOpaque = 0
    const hairFrontOpaque = 0

    let hairMesh = null
    const hairAnchorOffsetRatio = 0.025
    const hairMeshOptions = (() => {
      const quality = settings?.meshQuality || 'high'
      if (quality === 'performance') return { minStep: 8, maxStep: 16 }
      if (quality === 'balanced') return { minStep: 6, maxStep: 14 }
      return { minStep: 4, maxStep: 12 }
    })()
    if (hairOpaque > 40) {
      hairMesh = buildHairMeshFromAlpha(hairOnlyAlpha, width, height, hairMeshOptions)
      if (hairMesh?.points?.length && hairMesh?.triangles?.length) {
        const depth = computeHairMeshDepth(hairMesh.points, avatarRig, { falloff: 1.4 })
        hairMesh.depth = depth
        hairMesh.anchorOffsetRatio = hairAnchorOffsetRatio

        // Precompute nearest face landmarks for hair mesh warping (k-NN)
        const facePts = avatarRig?.pointsPx || []
        const faceOvalIdx = (FACE_MESH_CONTOUR_PATHS?.faceOval || []).filter((v) =>
          Number.isInteger(v)
        )
        const candidateIdx = faceOvalIdx.length ? faceOvalIdx : null
        const k = 6
        const maxDist = (avatarRig?.faceMetrics?.faceW || 120) * 1.4
        const maxDist2 = maxDist * maxDist
        const sigma = (avatarRig?.faceMetrics?.faceW || 120) * 0.45
        const sigma2 = Math.max(1, sigma * sigma * 2)
        const count = hairMesh.points.length
        const neighborIdx = new Uint16Array(count * k)
        const neighborW = new Float32Array(count * k)
        const neighborFalloff = new Float32Array(count)
        for (let i = 0; i < count; i += 1) {
          const p = hairMesh.points[i]
          const bestIdx = new Array(k).fill(-1)
          const bestD2 = new Array(k).fill(Infinity)
          let minD2 = Infinity
          const sourceIdx = candidateIdx || null
          const totalCandidates = sourceIdx ? sourceIdx.length : facePts.length
          for (let ci = 0; ci < totalCandidates; ci += 1) {
            const j = sourceIdx ? sourceIdx[ci] : ci
            const fp = facePts[j]
            if (!fp) continue
            const dx = fp.x - p.x
            const dy = fp.y - p.y
            const d2 = dx * dx + dy * dy
            if (d2 < minD2) minD2 = d2
            if (d2 > maxDist2) continue
            let worst = 0
            for (let t = 1; t < k; t += 1) {
              if (bestD2[t] > bestD2[worst]) worst = t
            }
            if (d2 < bestD2[worst]) {
              bestD2[worst] = d2
              bestIdx[worst] = j
            }
          }
          let wsum = 0
          for (let t = 0; t < k; t += 1) {
            const idx = bestIdx[t]
            const d2 = bestD2[t]
            const w = idx >= 0 && Number.isFinite(d2) ? Math.exp(-d2 / sigma2) : 0
            neighborIdx[i * k + t] = idx >= 0 ? idx : 0
            neighborW[i * k + t] = w
            wsum += w
          }
          if (wsum > 0) {
            for (let t = 0; t < k; t += 1) neighborW[i * k + t] /= wsum
          } else {
            for (let t = 0; t < k; t += 1) {
              neighborIdx[i * k + t] = 0
              neighborW[i * k + t] = 0
            }
          }
          if (Number.isFinite(minD2) && minD2 < Infinity) {
            const dist = Math.sqrt(minD2)
            neighborFalloff[i] = Math.max(0, Math.min(1, 1 - dist / maxDist))
          } else {
            neighborFalloff[i] = 0
          }
        }
        hairMesh.neighborIdx = neighborIdx
        hairMesh.neighborW = neighborW
        hairMesh.neighborK = k
        hairMesh.neighborFalloff = neighborFalloff
        hairMesh.neighborBlend = 0.7
      } else {
        hairMesh = null
      }
    }

    let hairBleed =
      edgeQuality === 'off'
        ? {
            bleedAlphaMax: 170,
            bleedNeighborMin: 210,
            bleedIters: 2,
            decontaminateStrength: 0.55,
            decontaminateIters: 1
          }
        : edgeQuality === 'supersample'
          ? {
              bleedAlphaMax: 245,
              bleedNeighborMin: 160,
              bleedIters: 6,
              decontaminateStrength: 1,
              decontaminateIters: 3
            }
          : {
              bleedAlphaMax: 230,
              bleedNeighborMin: 180,
              bleedIters: 5,
              decontaminateStrength: 0.9,
              decontaminateIters: 2
            }
    if (settings?.hairEnhance === true) {
      hairBleed = {
        ...hairBleed,
        bleedAlphaMax: Math.max(245, Number(hairBleed.bleedAlphaMax) || 0),
        bleedIters: Math.max(6, Number(hairBleed.bleedIters) || 0),
        decontaminateStrength: 1,
        decontaminateIters: Math.max(3, Number(hairBleed.decontaminateIters) || 0)
      }
    }
    // Smooth jagged hair rim similar to face: apply a light closing and edge soften.
    const erodeMask = (alpha, r) => {
      const inv = new Uint8ClampedArray(alpha.length)
      for (let p = 0; p < alpha.length; p += 1) inv[p] = 255 - (alpha[p] || 0)
      const invDil = dilateMask(inv, width, height, Math.max(1, r || 1))
      const out = new Uint8ClampedArray(invDil.length)
      for (let p = 0; p < invDil.length; p += 1) out[p] = 255 - invDil[p]
      return out
    }
    const hairClosed = erodeMask(dilateMask(hairOnlyAlpha, width, height, 1), 1)
    const edgeFeather = Math.max(0, Math.min(6, Number(settings?.hairEdgeFeatherPx) || 2))
    const hairAlphaSmooth = softenAlphaEdges(hairClosed, width, height, { radius: edgeFeather })
    const edgeGamma = Math.max(0.6, Math.min(2.2, Number(settings?.hairEdgeFeatherGamma) || 1.0))
    // Gamma-shape the outer gradient to look more photographic (raster-like)
    const hairAlphaFinal = new Uint8ClampedArray(hairAlphaSmooth.length)
    for (let p = 0; p < hairAlphaSmooth.length; p += 1) {
      const a = (hairAlphaSmooth[p] || 0) / 255
      hairAlphaFinal[p] = clampByte(Math.pow(a, edgeGamma) * 255)
    }
    const rasterizeAlpha = (alpha, w, h, scale = 2) => {
      try {
        const s = Math.max(2, Math.min(4, Math.floor(scale)))
        const src = typeof OffscreenCanvas !== 'undefined' ? new OffscreenCanvas(w, h) : null
        const dst =
          typeof OffscreenCanvas !== 'undefined' ? new OffscreenCanvas(w * s, h * s) : null
        const fin = typeof OffscreenCanvas !== 'undefined' ? new OffscreenCanvas(w, h) : null
        if (!src || !dst || !fin) return alpha
        const sctx = src.getContext('2d', { willReadFrequently: true })
        const dctx = dst.getContext('2d')
        const fctx = fin.getContext('2d', { willReadFrequently: true })
        if (!sctx || !dctx || !fctx) return alpha
        const img = sctx.createImageData(w, h)
        for (let i = 0; i < w * h; i += 1) {
          const ai = alpha[i] || 0
          const di = i * 4
          img.data[di] = 255
          img.data[di + 1] = 255
          img.data[di + 2] = 255
          img.data[di + 3] = ai
        }
        sctx.putImageData(img, 0, 0)
        dctx.imageSmoothingEnabled = true
        if (typeof dctx.imageSmoothingQuality === 'string') dctx.imageSmoothingQuality = 'high'
        dctx.drawImage(src, 0, 0, w * s, h * s)
        fctx.imageSmoothingEnabled = true
        if (typeof fctx.imageSmoothingQuality === 'string') fctx.imageSmoothingQuality = 'high'
        fctx.drawImage(dst, 0, 0, w, h)
        const out = fctx.getImageData(0, 0, w, h)
        const arr = new Uint8ClampedArray(w * h)
        for (let i = 0; i < w * h; i += 1) arr[i] = out.data[i * 4 + 3]
        return arr
      } catch {
        return alpha
      }
    }
    const ssScale = Math.max(2, Math.min(4, Math.floor(Number(settings?.hairEdgeSupersample) || 3)))
    // Supersampled, raster-like hair alpha
    let hairAlphaRaster = rasterizeAlpha(hairAlphaFinal, width, height, ssScale)
    // Optional: symmetrize hair horizontally about face center by mirroring the denser side
    if (settings?.hairSymmetrize === true) {
      try {
        const cxPix = Math.round(
          avatarRig.faceMetrics?.centerX || Math.max(0, Math.min(width - 1, width * 0.5))
        )
        const mirrored = new Uint8ClampedArray(width * height)
        for (let y = 0; y < height; y += 1) {
          const row = y * width
          for (let x = 0; x < width; x += 1) {
            const mx = Math.max(0, Math.min(width - 1, 2 * cxPix - x))
            mirrored[row + x] = hairAlphaRaster[row + mx]
          }
        }
        const out = new Uint8ClampedArray(width * height)
        for (let i = 0; i < out.length; i += 1) out[i] = Math.max(hairAlphaRaster[i], mirrored[i])
        hairAlphaRaster = out
      } catch {
        if (debug) debug('[headBuilder] hair symmetrize skipped due to error')
      }
    }
    // Split overlap with face into back/front regions using lateral band vs top band rule
    const facePtsForSplit = avatarRig?.pointsPx || []
    const ovalIdxForSplit = (FACE_MESH_CONTOUR_PATHS?.faceOval || []).filter((v) =>
      Number.isInteger(v)
    )
    let topYForSplit = Infinity
    for (let i = 0; i < ovalIdxForSplit.length; i += 1) {
      const p = facePtsForSplit[ovalIdxForSplit[i]]
      if (p && Number.isFinite(p.y) && p.y < topYForSplit) topYForSplit = p.y
    }
    if (!Number.isFinite(topYForSplit))
      topYForSplit = avatarRig.faceMetrics?.centerY - (avatarRig.faceMetrics?.faceH || 120) * 0.52
    const cxSplit = avatarRig.faceMetrics?.centerX || avatarRig.imageWidth * 0.5
    const faceWSplit = avatarRig.faceMetrics?.faceW || Math.max(80, avatarRig.imageWidth * 0.35)
    const lateralBandSplit = faceWSplit * 0.22
    const topBandRatio = Math.max(0, Math.min(0.5, Number(settings?.hairTopBandRatio) || 0.18))
    const hairFrontAlpha = new Uint8ClampedArray(hairAlphaRaster)
    const hairBackAlpha = new Uint8ClampedArray(width * height)
    for (let y = 0; y < height; y += 1) {
      const row = y * width
      for (let x = 0; x < width; x += 1) {
        const idx = row + x
        if (!hairAlphaRaster[idx]) continue
        if (!faceOvalInflated[idx]) continue
        const isTopBand = y < topYForSplit + faceWSplit * topBandRatio
        const isLateral = Math.abs(x - cxSplit) > lateralBandSplit
        if (!isTopBand && isLateral) {
          hairBackAlpha[idx] = hairFrontAlpha[idx]
          hairFrontAlpha[idx] = 0
        }
      }
    }
    // Final safety: never show hair over the face mesh. Allow only the forehead/top band.
    for (let y = 0; y < height; y += 1) {
      const row = y * width
      for (let x = 0; x < width; x += 1) {
        const idx = row + x
        if (!faceAlphaFinal[idx]) continue
        // Always remove overlap from BACK hair
        if (hairBackAlpha[idx]) hairBackAlpha[idx] = 0
        // Remove overlap from FRONT hair except the top band (bangs allowed)
        const isTopBand = y < topYForSplit + faceWSplit * topBandRatio
        if (hairFrontAlpha[idx] && !isTopBand) hairFrontAlpha[idx] = 0
      }
    }
    const hairCanvas = createLayerFromAlpha(
      avatarImage.img,
      width,
      height,
      hairFrontAlpha,
      hairBleed
    )
    const hairBackCanvas = createLayerFromAlpha(
      avatarImage.img,
      width,
      height,
      hairBackAlpha,
      hairBleed
    )
    const hairFrontCanvas = hairCanvas
    const eyesCanvas = createLayerFromAlpha(avatarImage.img, width, height, eyeAlpha)
    const headCanvas = createLayerFromAlpha(avatarImage.img, width, height, headAlpha)
    const faceTriangles = filterTrianglesByAlpha(
      avatarRig.triangles,
      avatarRig.pointsPx,
      faceAlpha,
      width,
      height
    )
    const stableFaceTriangles =
      faceTriangles.length >= Math.floor(avatarRig.triangles.length * 0.58)
        ? faceTriangles
        : avatarRig.triangles
    const hairTriangles = filterTrianglesByAlpha(
      avatarRig.triangles,
      avatarRig.pointsPx,
      hairOnlyAlpha,
      width,
      height,
      { fallbackToOriginal: false }
    )
    const hairBackTriangles = filterTrianglesByAlpha(
      avatarRig.triangles,
      avatarRig.pointsPx,
      hairBackAlpha,
      width,
      height,
      { fallbackToOriginal: false }
    )
    const hairFrontTriangles = filterTrianglesByAlpha(
      avatarRig.triangles,
      avatarRig.pointsPx,
      hairFrontAlpha,
      width,
      height,
      { fallbackToOriginal: false }
    )
    // Build a narrow seam band where hair overlaps a slightly dilated face edge,
    // so hair can "wrap" and attach to the face boundaries (top/left/right) in camera space.
    const seamDilate = Math.max(0, Math.min(6, Math.floor(Number(settings?.hairSeamDilatePx) || 2)))
    const faceDilatedForSeam = dilateMask(faceAlphaFinal, width, height, seamDilate)
    const hairFrontSeam = new Uint8ClampedArray(width * height)
    for (let p = 0; p < width * height; p += 1) {
      const hf = hairOnlyAlpha[p] || 0
      const fd = faceDilatedForSeam[p] || 0
      hairFrontSeam[p] = hf > 0 && fd > 0 ? Math.max(hf, fd) : 0
    }
    const hairFrontSeamTriangles = filterTrianglesByAlpha(
      avatarRig.triangles,
      avatarRig.pointsPx,
      hairFrontSeam,
      width,
      height,
      { fallbackToOriginal: false }
    )
    const eyeTriangles = filterTrianglesByAlpha(
      avatarRig.triangles,
      avatarRig.pointsPx,
      eyeAlpha,
      width,
      height,
      { fallbackToOriginal: false }
    )
    const scleraTriangles = filterTrianglesByAlpha(
      avatarRig.triangles,
      avatarRig.pointsPx,
      scleraAlpha,
      width,
      height,
      { fallbackToOriginal: false }
    )
    const expressionTriangles = faceTriangles

    debug(
      `Avatar head layer ready (${source}; refineHair=${refinedHairMode} face=${countOpaque(faceAlpha)} faceNoIris=${countOpaque(faceNoIrisAlpha)} iris=${countOpaque(irisAlpha)} sclera=${countOpaque(scleraAlpha)} eyes=${countOpaque(eyeAlpha)} eyeMesh=${eyeMeshOpaque} eyeFallback=${eyeFallbackOpaque} hair=${hairOpaque} faceTris=${Math.floor(faceTriangles.length / 3)} eyeTris=${Math.floor(eyeTriangles.length / 3)} scleraTris=${Math.floor(scleraTriangles.length / 3)} hairTris=${Math.floor(hairTriangles.length / 3)} exprTris=${Math.floor(expressionTriangles.length / 3)})`
    )
    return {
      faceCanvas,
      faceNoIrisCanvas,
      scleraCanvas,
      irisCanvas,
      pupilPalette,
      hairCanvas,
      hairBackCanvas,
      hairFrontCanvas,
      faceOpaque,
      hairOpaque,
      hairBackOpaque,
      hairFrontOpaque,
      hairAnchorOffsetRatio,
      hairMesh,
      eyesCanvas,
      headCanvas,
      faceTriangles: stableFaceTriangles,
      eyeTriangles,
      scleraTriangles,
      irisTriangles,
      hairTriangles,
      hairBackTriangles,
      hairFrontTriangles,
      hairFrontSeamTriangles,
      expressionTriangles: stableFaceTriangles,
      source
    }
  } catch {
    debug('Avatar head layer build failed.')
    return null
  }
}

export default { buildAvatarHeadLayer }
